import type {Moment} from 'moment-timezone';
import moment from 'moment-timezone';

import {hasValue} from '@famly/stat_ts-utils_has-value';
import {exhaustiveCheck} from '@famly/stat_ts-utils_exhaustive-check';
import {toRecord} from 'web-app/util/array';
import {testOverlap} from 'web-app/react/schedule-break/helpers/date-range';
import {generate} from 'web-app/util/array';

import * as DateFormatter from './date-formatter';
import type * as Types from '../types';

/**
 * An invoicing period that may be partial, e.g. if a plan begins, changes,
 * or ends in the middle of a period.
 */
export type Period = {
    /** Period frequency. */
    frequency: Types.InitialBillingFrequency;
    /**
     * Start (inclusive) of the period aligned to the automatic invoicing
     * configuration.
     */
    periodStartDate: Moment;
    /**
     * End (exclusive) of the period aligned to the automatic invoicing
     * configuration.
     */
    periodEndDate: Moment;
    /** Start (inclusive) of the custom period. */
    customStartDate: Moment | null;
    /** End (exclusive) of the custom period. */
    customEndDate: Moment | null;
};

/**
 * Calculates the start date and the date of the first period to show in the
 * past invoices.
 */
export const calculateStartDate = (
    planStartDate: Moment,
    firstUnbilledDate: Moment | null,
    periodAlign: (date: Moment) => Moment | null,
    addPeriods: (date: Moment, delta: number) => Moment,
): Moment | null => {
    const isPeriodAligned = (date: Moment): boolean => date.isSame(periodAlign(date), 'day');
    if (hasValue(firstUnbilledDate)) {
        if (isPeriodAligned(firstUnbilledDate)) {
            return addPeriods(firstUnbilledDate, -1);
        }
        return periodAlign(firstUnbilledDate);
    }
    return planStartDate;
};

/** The title of a summary period as it appears in the UI. */
export const getPeriodTitle = (period: Period): string => {
    const formatDateRange = (period: Period) =>
        `${DateFormatter.textFormat(getStartOfPeriod(period))} - ${DateFormatter.textFormat(
            getEndOfPeriodInclusive(period),
        )}`;
    switch (period.frequency) {
        case 'monthly':
            return isFullPeriod(period) ? getStartOfPeriod(period).format('MMMM') : formatDateRange(period);
        case 'weekly':
            return formatDateRange(period);
        case 'biweekly':
            return formatDateRange(period);
        default:
            exhaustiveCheck(period.frequency);
            return '01-01-1971';
    }
};

export const getStartOfPeriod = (period: Period): Moment =>
    hasValue(period.customStartDate) ? period.customStartDate : period.periodStartDate;

export const getEndOfPeriodExclusive = (period: Period): Moment =>
    hasValue(period.customEndDate) ? period.customEndDate : period.periodEndDate;

const getEndOfPeriodInclusive = (period: Period): Moment =>
    (hasValue(period.customEndDate) ? period.customEndDate : period.periodEndDate).clone().subtract(1, 'day');

/**
 * Adds a number of periods to a given date.
 *
 * @param date The original date.
 * @param count The number of periods. Can be negative.
 * @param frequency The period size.
 * @returns The new date.
 */
export const addPeriods = (date: Moment, count: number, frequency: Types.InitialBillingFrequency): Moment => {
    switch (frequency) {
        case 'monthly':
            return moment(date).add(count, 'month');
        case 'biweekly':
            return moment(date).add(2 * count, 'week');
        case 'weekly':
            return moment(date).add(count, 'week');
        default:
            exhaustiveCheck(frequency);
            return date;
    }
};

/**
 * Chops a sequence of periods such that there will be a period that starts at
 * a given date. The other period dates are left untouched.
 *
 * Does nothing if the date is outside the range of periods.
 */
export const chopPeriods = (periods: Period[], date: Moment): Period[] =>
    periods.flatMap(period => chopPeriod(period, date));

export const chopPeriod = (period: Period, date: Moment): Period[] => {
    if (date.isBetween(getStartOfPeriod(period), getEndOfPeriodInclusive(period), 'day', '()')) {
        return [
            {...period, customEndDate: date},
            {...period, customStartDate: date},
        ];
    }
    return [period];
};

/**
 * Generates date periods between two dates with a given frequency.
 */
export const generateSummaryPeriods = (
    startDate: Moment,
    endDate: Moment,
    frequency: Types.InitialBillingFrequency,
    periodStartDate: Moment,
): Period[] => {
    return generate(periodStartDate, (periodStartDate: Moment) => {
        if (periodStartDate.isSameOrAfter(endDate)) {
            return null;
        }
        const periodEndDate = addPeriods(periodStartDate, 1, frequency);
        const customStartDate = periodStartDate.isBefore(startDate) ? startDate : null;
        const customEndDate = periodEndDate.isAfter(endDate) ? endDate : null;
        const period = {
            frequency,
            periodStartDate,
            periodEndDate,
            customStartDate,
            customEndDate,
        };
        return {
            state: periodEndDate,
            element: period,
        };
    });
};

/**
 * Calculates the start date of the period that contains `date`.
 *
 * Returns `undefined` if `frequency` is `'biweekly'` but there is no biweekly
 * entry in `billingFrequencies`.
 */
export const periodStartDateAtOrBefore = (
    date: Moment,
    billingFrequencies: Types.BillingFrequency[],
    frequency: Types.InitialBillingFrequency,
): Moment | undefined => {
    const biweeklyStartDate = getBiweeklyInvoiceStartDate(billingFrequencies);
    return periodStartDateAtOrBeforeForBiweeklyStartDate(date, biweeklyStartDate, frequency);
};

export const getBiweeklyInvoiceStartDate = (billingFrequencies: Types.BillingFrequency[]): Moment | undefined => {
    const billingFrequency = billingFrequencies.find(billingFrequency => billingFrequency.type === 'biweekly');
    if (!hasValue(billingFrequency) || billingFrequency.type !== 'biweekly') {
        return undefined;
    }
    switch (billingFrequency.autoBillingPeriod) {
        case 'NEXT':
            return moment(billingFrequency.startDate).startOf('isoWeek').add(1, 'weeks');
        case 'CURRENT':
            return moment(billingFrequency.startDate).startOf('isoWeek');
        case 'PREVIOUS':
            return moment(billingFrequency.startDate).startOf('isoWeek').subtract(2, 'weeks');
        default:
            return undefined;
    }
};
/**
 * Returns the start date of the period containing `date`.
 *
 * Returns `undefined` if `frequency` is `'biweekly'` and `biweeklyStartDate`
 * is not available.
 */
export const periodStartDateAtOrBeforeForBiweeklyStartDate = (
    date: Moment,
    biweeklyStartDate: Moment | undefined,
    frequency: Types.InitialBillingFrequency,
): Moment | undefined => {
    switch (frequency) {
        case 'monthly':
            return date.clone().startOf('month');
        case 'weekly':
            return date.clone().startOf('isoWeek');
        case 'biweekly': {
            if (!hasValue(biweeklyStartDate)) {
                return undefined;
            }
            const weekCount = Math.floor(date.diff(biweeklyStartDate, 'week', true) / 2) * 2;
            return biweeklyStartDate.clone().add(weekCount, 'week');
        }
        default:
            exhaustiveCheck(frequency);
            return date;
    }
};

export type Plan = Types.Child['plans'][number];

type PlanLikeWithDates = Pick<Plan, 'startDate' | 'endDate'>;

/**
 * Gets all plans overlapping with a date range.
 * @param plans The plans to search.
 * @param from First date in date range (inclusive).
 * @param to End date in date range (exclusive).
 * @return Zero or more plans overlapping with the date range.
 */
export const getPlansBetween = <TPlan extends PlanLikeWithDates>(plans: TPlan[], from: Moment, to: Moment): TPlan[] =>
    plans.filter(planOverlaps(from, to));

/**
 * Tests if a plan is active at some point in a date range.
 * @param from Date range start (inclusive).
 * @param to Date range end (exclusive).
 * @param plan The plan to test.
 * @returns True iff the plan overlaps the date range.
 */
const planOverlaps =
    (from: Moment, to: Moment) =>
    (plan: PlanLikeWithDates): boolean =>
        testOverlap({fromInclusive: plan.startDate, toExclusive: plan.endDate}, {fromInclusive: from, toExclusive: to});

export type PlanLikeWellPricable = Pick<Plan, 'startDate' | 'endDate' | 'billingFrequency' | 'fees' | 'discounts'> & {
    tuitions: string[];
};

export type WellPricedPeriod =
    | {
          wellPriced: true;
      }
    | {
          wellPriced: false;
          reason: Types.PriceQualityReason;
      };

/**
 * Builds a well-pricable plan with custom tuitions, fees, and discounts.
 *
 * TODO: This is now vacuous. I think calculateWellPricedPeriodForChildGroups
 * should be refactored so this can be trashed.
 */
export const makeWellPricablePlanWithCustomPayables = (
    startDate: Moment,
    endDate: Moment | null,
    billingFrequency: Types.InitialBillingFrequency,
    tuitions: string[],
    fees: string[],
    discounts: string[],
): PlanLikeWellPricable => ({
    startDate,
    endDate,
    billingFrequency,
    tuitions,
    fees,
    discounts,
});

/**
 * Merges two `WellPricedPeriod`s. The result is well-priced only if both
 * arguments are well-priced. Picks a reason if both are not well-priced.
 */
export const mergeWellPricedPeriods = (left: WellPricedPeriod, right: WellPricedPeriod): WellPricedPeriod => {
    if (!left.wellPriced) {
        return left;
    }
    if (!right.wellPriced) {
        return right;
    }
    return left;
};

/**
 * Associated true to each period if we can estimate the invoice amounts to
 * the period. Associate false if the period can not (yet) be given a good
 * price, e.g. if the period is prorated or needs resampling.
 */
export const calculateWellPricedPeriods = ({
    periods,
    plans,
    tuitionPeriodById,
    discountPeriodById,
    feePeriodById,
}: {
    periods: Period[];
    plans: PlanLikeWellPricable[];
    tuitionPeriodById: (tuitionId: string) => Types.BillingType | undefined;
    discountPeriodById: (discountId: string) => Types.BillingType | undefined;
    feePeriodById: (feeId: string) => Types.BillingType | undefined;
}): WellPricedPeriod[] =>
    periods.map(period => isWellPricedPeriod(period, plans, tuitionPeriodById, discountPeriodById, feePeriodById));

/**
 * Tests to see if the prices can be calculated for a period and gives a
 * reason why not.
 */
export const isWellPricedPeriod = (
    period: Period,
    plans: PlanLikeWellPricable[],
    tuitionPeriodById: (tuitionId: string) => Types.BillingType | undefined,
    discountPeriodById: (discountId: string) => Types.BillingType | undefined,
    feePeriodById: (feeId: string) => Types.BillingType | undefined,
): WellPricedPeriod => {
    if (!isFullPeriod(period)) {
        return {wellPriced: false, reason: 'customPeriod'};
    }

    const candidatePlans = getPlansBetween(plans, getStartOfPeriod(period), getEndOfPeriodExclusive(period));
    if (candidatePlans.length === 0) {
        return {wellPriced: false, reason: 'noPlans'};
    }
    if (candidatePlans.length > 1) {
        return {wellPriced: false, reason: 'multiplePlans'};
    }
    const plan = candidatePlans[0];

    const sameFrequency = plan.billingFrequency === period.frequency;
    const canUpsampleToBiweekly = period.frequency === 'biweekly' && plan.billingFrequency === 'weekly';
    if (!sameFrequency && !canUpsampleToBiweekly) {
        return {wellPriced: false, reason: 'billingFrequencyMismatch'};
    }

    const verifyBillingFrequencyMatchesPeriod: (billingType: Types.BillingType) => boolean =
        period.frequency === 'biweekly'
            ? billingType => billingType === 'weekly'
            : billingType => billingType === period.frequency;
    if (
        !verifyPlanPeriods(
            plan,
            tuitionPeriodById,
            discountPeriodById,
            feePeriodById,
            verifyBillingFrequencyMatchesPeriod,
        )
    ) {
        return {wellPriced: false, reason: 'payableBillingFrequencyMismatch'};
    }

    return {wellPriced: true};
};

/** Gets the period of a tuition or undefined if missing. */
export const getTuitionPeriod =
    (tuitionById: (id: string) => Types.Tuition | undefined) =>
    (tuitionId: string): Types.BillingType | undefined => {
        const tuition = tuitionById(tuitionId);
        return hasValue(tuition) ? tuition.type : undefined;
    };

/** Gets the period of a fee or undefined if missing. */
export const getFeePeriod =
    (feeById: (id: string) => Types.Fee | undefined) =>
    (feeId: string): Types.BillingType | undefined => {
        const fee = feeById(feeId);
        return hasValue(fee) ? fee.type : undefined;
    };

/** Gets the period of a discount or undefined if missing. */
export const getDiscountPeriod =
    (discountById: (id: string) => Types.Discount | undefined) =>
    (discountId: string): Types.BillingType | undefined => {
        const discount = discountById(discountId);
        if (!hasValue(discount)) {
            return undefined;
        }
        return splitDiscount(
            discount,
            () => undefined,
            percentDiscount => percentDiscount.period,
            amountDiscount => amountDiscount.period,
            undefined,
        );
    };

const isFullPeriod = (period: Period): boolean => {
    return !hasValue(period.customStartDate) && !hasValue(period.customEndDate);
};

/**
 * Verifies all the billing periods of tuitions, fees, and discounts by a
 * given predicate in the plan. Missing periods are taken as validated.
 */
const verifyPlanPeriods = (
    plan: PlanLikeWellPricable,
    tuitionPeriodById: (tuitionId: string) => Types.BillingType | undefined,
    discountPeriodById: (discountId: string) => Types.BillingType | undefined,
    feePeriodById: (feeId: string) => Types.BillingType | undefined,
    verifyPeriod: (period: Types.BillingType) => boolean,
): boolean => {
    const verifyTuition = (tuitionId: string): boolean => {
        const period = tuitionPeriodById(tuitionId);
        return hasValue(period) ? verifyPeriod(period) : true;
    };
    const verifyFee = (feeId: string): boolean => {
        const period = feePeriodById(feeId);
        return hasValue(period) ? verifyPeriod(period) : true;
    };
    const verifyDiscount = (discountId: string): boolean => {
        const period = discountPeriodById(discountId);
        return hasValue(period) ? verifyPeriod(period) : true;
    };
    return plan.tuitions.every(verifyTuition) && plan.fees.every(verifyFee) && plan.discounts.every(verifyDiscount);
};

/**
 * Find the pricing group that is effect. Will choose a fixed pricing group on
 * a manual pricing group configuration. Will choose the pricing group that is
 * in effect at the given date on an automatic pricing group configuration.
 *
 * @param planPricing The pricing configuration.
 * @param automaticPricingGroupRules Pricing group rules used when automatic pricing group is in effect.
 * @param date The date on which the pricing group should apply.
 * @param detailedPricingGroups The available pricing groups.
 * @return The active pricing group or null if no pricing group could be resolved.
 */
export const findActivePricingGroupForPricingConfig = (
    planPricing: Types.PlanPricing | null,
    automaticPricingGroupRules: Types.PricingGroupForChild[],
): ((date: Moment) => (detailedPricingGroups: Types.PricingGroup[]) => Types.PricingGroup | null) => {
    if (hasValue(planPricing) && planPricing.type === 'manual') {
        return findActivePricingGroupForManual(planPricing.pricingGroup);
    }
    return findActivePricingGroupForAutomaticRules(automaticPricingGroupRules);
};

const findActivePricingGroupForManual =
    (manualPricingGroupId: string) =>
    (/* date: Moment*/) =>
    (detailedPricingGroups: Types.PricingGroup[]): Types.PricingGroup | null => {
        return detailedPricingGroups.find(group => group.id === manualPricingGroupId) ?? null;
    };

const findActivePricingGroupForAutomaticRules =
    (automaticPricingGroupRules: Types.PricingGroupForChild[]) =>
    (date: Moment) =>
    (detailedPricingGroups: Types.PricingGroup[]): Types.PricingGroup | null => {
        const activePricingGroupIds = automaticPricingGroupRules
            .filter(
                pricingGroup =>
                    pricingGroup.from.isSameOrBefore(date) &&
                    (pricingGroup.to === null || date.isSameOrBefore(pricingGroup.to)),
            )
            .map(pricingGroup => pricingGroup.id);

        const pricingGroupCandidates = activePricingGroupIds
            .map(
                pricingGroupId =>
                    detailedPricingGroups.find(pricingGroup => pricingGroup.id === pricingGroupId) ?? null,
            )
            .filter(hasValue);

        if (pricingGroupCandidates.length !== 1) {
            return null;
        }
        return pricingGroupCandidates[0];
    };

export type TuitionAndFeeLike = Pick<Types.Tuition | Types.Fee, 'id' | 'pricingGroups'>;

/**
 * For tuitions and fees, generate an index for each summary period of the
 * relevant pricing group for all tuitions/fees.
 * @param T A type that has an `id` and `pricingGroups` like `Types.Tuition`
 * or `Types.Fee`.
 * @param findActivePricingGroup A function that can choose the active pricing
 * group.
 * @param entities The entities (`Type.Tuition` or `Type.Fee`) to index.
 * @param A record that maps entity ids to active pricing groups.
 */
export const generateEntityPricingGroupIndex = (
    findActivePricingGroup: (pricingGroups: Types.PricingGroup[]) => Types.PricingGroup | null,
    entities: TuitionAndFeeLike[],
): {[entityId: string]: Types.PricingGroup} => {
    const entityPairs = entities
        .map(entity => {
            const pricingGroup = findActivePricingGroup(entity.pricingGroups);
            if (!hasValue(pricingGroup)) {
                return null;
            }
            return {entityId: entity.id, pricingGroup};
        })
        .filter(hasValue);
    return toRecord(
        entityPairs,
        entity => entity.entityId,
        entity => entity.pricingGroup,
    );
};

/** True if a tuition or fee changes pricing group between the inputs. */
export const hasEntityWithDifferentPricingGroup = (
    current: {[entityId: string]: Types.PricingGroup},
    next: {[entityId: string]: Types.PricingGroup},
): boolean => {
    return Object.entries(current).some(([entityId, currentPricingGroup]) => {
        const nextPricingGroup = next[entityId];
        return hasValue(nextPricingGroup) && nextPricingGroup.id !== currentPricingGroup.id;
    });
};

/**
 * Calculates the summary items for the discounts. Also updates the total
 * according to the discounts in the order of discount `ordering`.
 */
export const calculateDiscountSummaryItems = (
    discounts: Types.Discount[],
    totalBeforeDiscounts: number,
    periodFrequency: Types.InitialBillingFrequency,
): {total: number; discountSummaryItems: Types.SummaryPeriodItem[]} => {
    // TODO: This is problematic for multipart plans as multiple discounts
    // in different parts could have the same ordering leading to an
    // indeterministic order of discounts.
    const orderedDiscounts = discounts.sort((discount1, discount2) => discount1.ordering - discount2.ordering);

    const calculateAndAppendDiscountSummaryItem = (
        {total, discountSummaryItems}: {total: number; discountSummaryItems: Types.SummaryPeriodItem[]},
        discount: Types.Discount,
    ): {total: number; discountSummaryItems: Types.SummaryPeriodItem[]} => {
        const {amount, summaryItem} = calculateDiscountSummaryItem(discount, total, periodFrequency);
        return {
            total: total - amount,
            discountSummaryItems: [...discountSummaryItems, summaryItem],
        };
    };

    return orderedDiscounts.reduce(calculateAndAppendDiscountSummaryItem, {
        total: totalBeforeDiscounts,
        discountSummaryItems: [],
    });
};

/**
 * Generates a summary item from a discount. Also returns the absolute
 * discount amount from a running total.
 */
export const calculateDiscountSummaryItem = (
    discount: Types.Discount,
    runningTotal: number,
    periodFrequency: Types.InitialBillingFrequency,
): {amount: number; summaryItem: Types.SummaryPeriodItem} =>
    splitDiscount(
        discount,
        presetDiscount => calculateRelativeDiscountSummaryItem(runningTotal, presetDiscount),
        percentageDiscount => calculateRelativeDiscountSummaryItem(runningTotal, percentageDiscount),
        amountDiscount =>
            calculateAbsoluteDiscountSummaryItem(
                amountDiscount,
                periodFrequency === 'biweekly' && amountDiscount.period === 'weekly' ? 2 : 1,
            ),
        {
            amount: 0,
            summaryItem: {
                name: '',
                pricingGroups: [],
            },
        },
    );

/** Case splitter for discounts on preset, percentage, and amount discounts. */
const splitDiscount = <T>(
    discount: Types.Discount,
    onPreset: (discount: Types.DiscountPreset) => T,
    onPercentage: (discount: Types.DiscountCustom) => T,
    onAmount: (discount: Types.DiscountCustom) => T,
    fallback: T,
): T => {
    switch (discount.type) {
        case 'custom':
            switch (discount.customDiscountType) {
                case 'amount':
                    return onAmount(discount);
                case 'percentage':
                    return onPercentage(discount);
                default:
                    exhaustiveCheck(discount.customDiscountType);
                    return fallback;
            }
        case 'preset': {
            return onPreset(discount);
        }
        default:
            exhaustiveCheck(discount);
            return fallback;
    }
};

const calculateAbsoluteDiscountSummaryItem = (
    discount: Types.BaseDiscount,
    factor: number,
): {amount: number; summaryItem: Types.SummaryPeriodItem} => {
    return {
        amount: factor * discount.amount,
        summaryItem: {
            name: discount.title,
            pricingGroups: [
                {
                    id: discount.id,
                    price: factor * discount.amount,
                    children: null,
                },
            ],
        },
    };
};

const calculateRelativeDiscountSummaryItem = (
    total: number,
    discount: Types.BaseDiscount,
): {amount: number; summaryItem: Types.SummaryPeriodItem} => {
    const amount = discount.amount * total;
    return {
        amount,
        summaryItem: {
            name: discount.title,
            pricingGroups: [
                {
                    id: discount.id,
                    price: amount,
                    children: null,
                },
            ],
        },
    };
};

export const showPeriod = (period: Period): string =>
    `${period.periodStartDate.format('YYYY-MM-DD')}(${period.customStartDate?.format(
        'YYYY-MM-DD',
    )}) - ${period.periodEndDate.format('YYYY-MM-DD')}(${period.customEndDate?.format('YYYY-MM-DD')}) ${
        period.frequency
    }`;
